import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { CSSTransition } from 'react-transition-group';

import type { SocialTrackingMetadataInput } from '@xing-com/crate-common-graphql-types';
import { ShareActionSheet } from '@xing-com/crate-social-share';
import {
  LikeReactionButton,
  useCachedInteractionTarget,
  useIsSmallScreen,
  useOptimisticReactionsUpdate,
  useWidth,
} from '@xing-com/crate-social-utils';

import {
  type CommentReactionProps,
  CommentReactionButton,
} from '../comment-reaction-button/comment-reaction-button';
import { SocialCounters } from '../social-counters/social-counters';
import { ToolTip } from '../tooltip/tooltip';

import {
  Styled,
  socialBarTransitionName,
  socialBarAnimationDuration,
} from './social-bar.styles';

export type SocialBarProps = React.ComponentProps<typeof Styled.SocialBar> &
  CommentReactionProps & {
    /** tracking, ask your PO / DA required for share */
    backendTrackingMetaData?: SocialTrackingMetadataInput;
    /** Add custom CSS  */
    className?: string;
    /** @callback comment button for counters and social bar */
    commentClick?(): void;
    /** tracking, ask your PO / DA entry point tracking share to messenger */
    entryPoint?: string;
    /** disables the comment button  */
    hasComment?: boolean;
    /** disables the like button  */
    hasReaction?: boolean;
    /** disables the share button  */
    hasShare?: boolean;
    /** disables the views counter  */
    hasViewCounter?: boolean;
    /** tracking, DISCO-only  */
    inputHintClick?: boolean;
    /** optional menu component that displays at the left side */
    leftBar?: React.ReactNode;
    /** @callback comment button on social bar */
    onCommentClick?: () => void;
    /** @callback like button. The isUserLiking param exposes if the user is liking */
    onReactionClick?: (isUserLiking?: boolean) => void;
    /** @callback share button */
    onShareClick?: () => void;
    /** tracking, ask your PO / DA */
    propChannel: string;
    /** tracking type to differentiate between different entity page types */
    propSourceType?: string;
    /** optional menu component that displays at the right side */
    rightBar?: React.ReactNode;
    /** URL for share preview required for share */
    url?: string;
    /** Resocurce name of the activity */
    urn: string;
    trackingToken?: string | null;
  };

export const SocialBar: React.FC<SocialBarProps> = ({
  backendTrackingMetaData,
  className,
  commentClick,
  commentTo,
  entryPoint = '',
  hasComment = true,
  hasReaction = true,
  hasShare = true,
  hasViewCounter = false,
  inputHintClick = false,
  leftBar,
  onCommentClick = () => undefined,
  onReactionClick = () => undefined,
  onShareClick = () => undefined,
  propChannel,
  propSourceType,
  rightBar,
  url = '',
  urn,
  trackingToken,
  section,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const interactionTarget = useCachedInteractionTarget(urn);
  const { handleOnComplete } = useOptimisticReactionsUpdate(
    propChannel,
    urn,
    inputHintClick
  );
  const { width, ref } = useWidth();
  // smallcaps from props - CamelCase for Components to be rendered
  const [LeftBar, RightBar] = [leftBar, rightBar];

  // Resize buttons size when there's no space available
  const [compact, setCompact] = React.useState(false);

  React.useEffect(() => {
    // Minimum social bar size that doesn't overflow with full counters (all 4 counters with value of 999).
    const socialBarBreakPoint = 354;
    setCompact(width < socialBarBreakPoint || isSmallScreen);
  }, [isSmallScreen, width]);

  const likeProps = {
    ...(hasReaction
      ? {
          onReactionClick,
          counter: interactionTarget.reactionsCount,
          hasLike: interactionTarget.userReactionType,
        }
      : { counter: 0, hasLike: false }),
  };

  const commentProps = {
    ...(hasComment
      ? {
          counter: interactionTarget.commentsCount,
          ...(commentTo && { to: commentTo }),
          commentClick,
          onCommentClick,
        }
      : {
          counter: 0,
          to: undefined,
          href: undefined,
          commentClick: () => undefined,
        }),
  };

  const shareProps = hasShare
    ? {
        counter: interactionTarget.sharesCount,
        interactionTargetUrn: urn,
        onShareClick,
        url,
      }
    : {
        counter: 0,
        interactionTargetUrn: '',
        onShareClick,
        url: '',
      };

  const hasCounters = !!(
    likeProps.counter ||
    commentProps.counter ||
    shareProps.counter ||
    hasViewCounter
  );

  return (
    <CSSTransition
      appear={hasCounters}
      classNames={socialBarTransitionName}
      in={hasCounters}
      timeout={parseInt(socialBarAnimationDuration)}
      nodeRef={ref}
    >
      <Styled.SocialBar className={className} ref={ref}>
        <SocialCounters
          {...{ compact }}
          comments={{ ...commentProps }}
          likes={{
            counter: likeProps.counter,
            propChannel,
            urn,
          }}
          shares={{ counter: shareProps.counter }}
          views={{ hasViewCounter, urn }}
        />
        <Styled.SocialReactions>
          <Styled.SocialReactionsLeft {...{ compact }}>
            {hasReaction && (
              <ToolTip
                onClick={() => onReactionClick(likeProps.hasLike === null)}
                id="LIKE_BUTTON_LABEL"
              >
                <LikeReactionButton
                  {...{ urn, backendTrackingMetaData }}
                  hasLike={!!likeProps.hasLike}
                  label={
                    !compact && <FormattedMessage id="LIKE_BUTTON_LABEL" />
                  }
                  onCompleted={handleOnComplete}
                />
              </ToolTip>
            )}
            {hasComment && (
              <ToolTip onClick={onCommentClick} id="COMMENT_BUTTON_LABEL">
                <CommentReactionButton
                  {...{ compact }}
                  {...{
                    entryPoint: backendTrackingMetaData?.entryPoint ?? section,
                  }}
                  {...(commentTo && { to: commentTo })}
                  onClick={commentClick}
                />
              </ToolTip>
            )}
            {hasShare && shareProps?.interactionTargetUrn && (
              <ToolTip id="SHARE_BUTTON_LABEL">
                <ShareActionSheet
                  {...{
                    backendTrackingMetaData,
                    entryPoint,
                    inputHintClick,
                    interactionTargetUrn: shareProps.interactionTargetUrn,
                    onButtonClick: shareProps.onShareClick,
                    propChannel,
                    propSourceType,
                    url: shareProps.url,
                    itemUrn: urn,
                    trackingToken,
                  }}
                  hasQuickShare
                  label={
                    !compact && <FormattedMessage id="SHARE_BUTTON_LABEL" />
                  }
                />
              </ToolTip>
            )}
            {LeftBar}
          </Styled.SocialReactionsLeft>
          {RightBar && <div>{RightBar}</div>}
        </Styled.SocialReactions>
      </Styled.SocialBar>
    </CSSTransition>
  );
};

SocialBar.displayName = 'SocialBar';
